// A league's invites by email (PLAN-2026-10-28-by-email §3.1, scenario B2,
// NOTIFICATIONS.md §5): a commissioner types addresses, each gets one email
// saying who invited them to what, and the commissioners see who has not
// joined yet.

import { z } from 'zod';

const Iso = z.string();

/** The most addresses one send takes: a league's worth, and a paste somebody can check. */
export const INVITES_PER_SEND = 10;

/**
 * An invite by email, as its league's commissioners see it, and nobody else:
 * the address is personal data, kept on the invite only until it ends (`0008`).
 */
export const EmailInvite = z.object({
  id: z.string(),
  /** The address it went to, as the commissioner typed it. */
  address: z.string(),
  /**
   * When the email went. Null for one that has not gone — the send failed, or
   * the site's day of invite mail was spent — which may be sent again.
   */
  sentAt: Iso.nullable(),
  /** Who sent it, by display name; null once that account is gone. */
  invitedBy: z.string().nullable(),
});
export type EmailInvite = z.infer<typeof EmailInvite>;

/** The pending list on its own: what taking an invite back answers. */
export const PendingInvites = z.object({ invites: z.array(EmailInvite) });
export type PendingInvites = z.infer<typeof PendingInvites>;

/** `POST /api/leagues/:id/invites`: the addresses to invite, one to ten. */
export const SendInvitesRequest = z.object({
  addresses: z.array(z.string().trim().min(1).max(254)).min(1).max(INVITES_PER_SEND),
});
export type SendInvitesRequest = z.infer<typeof SendInvitesRequest>;

/**
 * Why an address was not invited, each in its own words (§3.1):
 *
 * - `invalid` is not an address;
 * - `member` signs in to an account already in the league;
 * - `invited` was emailed an invite to this league before (once, ever);
 * - `opted-out` asked for no league invites (§3.2);
 * - `undeliverable` does not take mail: an email to it bounced (§3.2);
 * - `limit` is past the league's or the sender's invites for the day;
 * - `unsent` was written down and its email did not go: send it again later.
 */
export const InviteRefusalReason = z.enum([
  'invalid',
  'member',
  'invited',
  'opted-out',
  'undeliverable',
  'limit',
  'unsent',
]);
export type InviteRefusalReason = z.infer<typeof InviteRefusalReason>;

/** What a send did: who was emailed, who was not and why, and the pending list after it. */
export const InvitesSent = z.object({
  sent: z.array(z.string()),
  refused: z.array(
    z.object({
      address: z.string(),
      reason: InviteRefusalReason,
      /** The reason as a sentence, for a client to show as written. */
      message: z.string(),
    }),
  ),
  invites: z.array(EmailInvite),
});
export type InvitesSent = z.infer<typeof InvitesSent>;

/**
 * What an invite's opt-out link names (`GET`, `POST /api/email/invites-stop?k=`):
 * whether league invites to its address are stopped. No address and no name,
 * because the email may have been forwarded.
 */
export const InvitesStopView = z.object({
  stopped: z.boolean(),
});
export type InvitesStopView = z.infer<typeof InvitesStopView>;
